import { useId } from 'react';
import type { Platform } from '@dintiko/shared';
import { cx } from '../lib/cx.js';

/**
 * The platforms' own marks (PLAN §13.2).
 *
 * This replaces the two-letter placeholders. Each is drawn as a tile with the
 * platform's brand colour and its glyph in white — the shape every one of them
 * ships as an app icon, which is how the owner recognises them on a phone.
 *
 * Drawn as inline SVG rather than bitmaps on purpose: no network request (the
 * app's CSP forbids remote images anyway), no asset licensing in the
 * repository, crisp at any size, and about a kilobyte for all five.
 *
 * Used only to say "this post goes here", which is the use every one of these
 * brand guidelines permits. The marks are never altered, recoloured, or set
 * below their legible size.
 */

const BRAND: Record<Platform, { label: string; tile: string }> = {
  youtube: { label: 'YouTube', tile: '#ff0000' },
  instagram: { label: 'Instagram', tile: 'url(#ig)' },
  tiktok: { label: 'TikTok', tile: '#000000' },
  facebook: { label: 'Facebook', tile: '#1877f2' },
  linkedin: { label: 'LinkedIn', tile: '#0a66c2' },
  telegram: { label: 'Telegram', tile: 'url(#tg)' },
  mock: { label: 'Mock', tile: '#59636d' },
};

/**
 * How much of the tile each glyph fills.
 *
 * Drawn at their natural sizes the five do not read as a set: TikTok's note is
 * tall and thin, YouTube's triangle is small and wide, and Instagram's outline
 * is a square that looks larger than either. These scale each one about the
 * tile's centre until the *ink* looks the same weight, which is what "the same
 * size" means to an eye.
 */
const FILL: Record<Platform, { scale: number; dx?: number; dy?: number }> = {
  // The note's bounding box is wider than its stem, so centring the box
  // leaves the glyph looking pushed right. Nudged back by eye.
  tiktok: { scale: 0.74, dx: -0.5, dy: 0.3 },
  facebook: { scale: 0.92 },
  linkedin: { scale: 0.9 },
  // A triangle reads smaller than its box, but 1.3 made it dominate the tile.
  youtube: { scale: 1.08 },
  instagram: { scale: 0.94 },
  // The plane points up-right, so its bounding box carries empty corners;
  // left at its natural size it reads smaller than the others.
  telegram: { scale: 1.02, dy: 0.2 },
  mock: { scale: 1 },
};

/** Each glyph is drawn inside a 24×24 box, white on the brand tile. */
function Glyph({ platform }: { platform: Platform }) {
  switch (platform) {
    case 'telegram':
      // The paper plane: a swept body with the fold shading its underside.
      return (
        <path
          fill="#fff"
          d="M4.9 11.6 18.2 6.5c.62-.22 1.16.15.96 1.08l-2.27 10.7c-.17.78-.63.97-1.28.6l-3.53-2.6-1.7 1.64c-.19.19-.35.35-.71.35l.25-3.6 6.56-5.93c.29-.25-.06-.4-.44-.15l-8.1 5.1-3.5-1.09c-.76-.24-.77-.76.16-1.13Z"
        />
      );

    case 'tiktok':
      return (
        <path
          fill="#fff"
          d="M16.5 5.9a4.3 4.3 0 0 1-1.05-2.8h-2.98v11.98a2.42 2.42 0 1 1-2.42-2.42c.25 0 .49.04.72.11V9.73a5.46 5.46 0 0 0-.72-.05 5.44 5.44 0 1 0 5.44 5.44V9.13a7.2 7.2 0 0 0 4.08 1.28V7.47a4.27 4.27 0 0 1-3.07-1.57Z"
        />
      );
    case 'facebook':
      return (
        <path
          fill="#fff"
          d="M13.4 21.6v-8.4h2.45l.37-2.85H13.4V8.53c0-.82.23-1.39 1.41-1.39h1.5V4.6a20.3 20.3 0 0 0-2.19-.11c-2.17 0-3.66 1.33-3.66 3.76v2.1H7.99v2.85h2.47v8.4Z"
        />
      );
    case 'linkedin':
      return (
        <g fill="#fff">
          <path d="M6.9 9.7h2.65v8.6H6.9z" />
          <circle cx="8.22" cy="6.9" r="1.55" />
          <path d="M11.45 9.7h2.54v1.18h.04c.36-.67 1.22-1.38 2.5-1.38 2.68 0 3.17 1.76 3.17 4.05v4.75h-2.65v-4.21c0-1-.02-2.3-1.4-2.3-1.4 0-1.61 1.1-1.61 2.23v4.28h-2.65Z" />
        </g>
      );
    case 'youtube':
      return <path fill="#fff" d="M10.1 8.6 16.4 12l-6.3 3.4Z" />;
    case 'instagram':
      return (
        <g fill="none" stroke="#fff" strokeWidth="1.7">
          <rect x="5.6" y="5.6" width="12.8" height="12.8" rx="4" />
          <circle cx="12" cy="12" r="3.25" />
          <circle cx="16.2" cy="7.9" r="0.95" fill="#fff" stroke="none" />
        </g>
      );
    default:
      return (
        <text
          x="12"
          y="16"
          textAnchor="middle"
          fill="#fff"
          fontSize="9"
          fontFamily="monospace"
          fontWeight="600"
        >
          MK
        </text>
      );
  }
}

function transformFor(platform: Platform): string {
  const { scale, dx = 0, dy = 0 } = FILL[platform];
  return `translate(${String(12 + dx)} ${String(12 + dy)}) scale(${String(scale)}) translate(-12 -12)`;
}

export function PlatformLogo({
  platform,
  size = 16,
  className,
  onPanel = false,
}: {
  platform: Platform;
  size?: number;
  className?: string;
  /**
   * Set on --aw-panel, which is black in both themes. Only TikTok cares: its
   * edge is normally --aw-line, and in the dark theme that is #262626 — on a
   * black panel the tile would lose its shape entirely.
   */
  onPanel?: boolean;
}) {
  // The Instagram gradient needs an id unique to this instance; two marks on
  // one screen sharing an id makes the second one reference the first's def.
  const gradientId = useId();
  const brand = BRAND[platform];
  // Two of the marks are gradients rather than flat colours. They share one
  // generated id per instance, suffixed, because two logos on the same screen
  // would otherwise both define `#ig` and the second would win.
  const tile =
    platform === 'instagram' || platform === 'telegram' ? `url(#${gradientId})` : brand.tile;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={brand.label}
      className={cx('shrink-0', className)}
    >
      {platform === 'instagram' && (
        <defs>
          {/* Instagram's mark is a gradient rather than a flat colour;
              flattening it would be altering the mark. */}
          <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#ffd521" />
            <stop offset="28%" stopColor="#f50000" />
            <stop offset="63%" stopColor="#b900b4" />
            <stop offset="100%" stopColor="#4e60d3" />
          </linearGradient>
        </defs>
      )}
      {platform === 'telegram' && (
        <defs>
          {/* Telegram's is the gentler of the two: a vertical fade from
              #2AABEE to #229ED9, top to bottom. */}
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2aabee" />
            <stop offset="100%" stopColor="#229ed9" />
          </linearGradient>
        </defs>
      )}
      <rect x="0" y="0" width="24" height="24" rx="5.4" fill={tile} />
      {/* TikTok's tile is black, which vanishes on the dark theme's black
          ground — the mark lost its shape and read as a loose glyph beside
          four platforms that kept theirs. A hairline in the ink colour gives
          it an edge back, and it is invisible on a light ground because it
          sits on black there anyway. */}
      {platform === 'tiktok' && (
        <rect
          x="0.6"
          y="0.6"
          width="22.8"
          height="22.8"
          rx="4.9"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          className={onPanel ? 'text-panel-muted' : 'text-line'}
        />
      )}
      <g transform={transformFor(platform)}>
        <Glyph platform={platform} />
      </g>
    </svg>
  );
}
